import { useT } from '@/shared/i18n';
import { Icon } from '@/shared/ui/icon';
import type { MouseEvent, ReactNode } from 'react';

/**
 * FeatureGrid — "Built for modern trajectories" (extraction §2/§14). Six
 * `.spotlight-card`s with Lime icon chips and a mouse-follow Lime glow.
 * Theme-adaptive via tokens.
 */
function SpotlightCard({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: string;
}): ReactNode {
  const onMouseMove = (e: MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--card-x', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--card-y', `${e.clientY - rect.top}px`);
  };
  return (
    <div
      onMouseMove={onMouseMove}
      className="spotlight-card group rounded-[32px] border border-outline-variant/30 bg-surface-container p-10 transition-all hover:border-secondary-fixed/50"
    >
      <div className="relative z-10">
        <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-xl bg-secondary-fixed text-on-secondary-fixed transition-transform group-hover:scale-110">
          {icon}
        </div>
        <h3 className="mb-4 text-2xl font-semibold text-on-surface">{title}</h3>
        <p className="leading-relaxed text-on-surface-variant">{body}</p>
      </div>
    </div>
  );
}

export function FeatureGrid(): ReactNode {
  const { t } = useT();
  const cards = [
    { icon: <Icon name="bar-chart" size={22} />, title: t('landing.dark.feature1.title'), body: t('landing.dark.feature1.body') },
    { icon: <Icon name="route" size={22} />, title: t('landing.dark.feature2.title'), body: t('landing.dark.feature2.body') },
    { icon: <Icon name="shield" size={22} />, title: t('landing.dark.feature3.title'), body: t('landing.dark.feature3.body') },
    { icon: <Icon name="languages" size={22} />, title: t('landing.dark.feature4.title'), body: t('landing.dark.feature4.body') },
    { icon: <Icon name="users" size={22} />, title: t('landing.dark.feature5.title'), body: t('landing.dark.feature5.body') },
    { icon: <Icon name="trending-up" size={22} />, title: t('landing.dark.feature6.title'), body: t('landing.dark.feature6.body') },
  ];
  return (
    <section id="features" className="scroll-mt-24 bg-surface-base py-32">
      <div className="mx-auto max-w-container-max px-margin-mobile md:px-margin-desktop">
        <div className="mb-20 text-center">
          <h2 className="mb-6 text-4xl font-bold tracking-tight text-on-surface md:text-5xl">
            {t('landing.dark.features.heading')}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-on-surface-variant">
            {t('landing.dark.features.sub')}
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => (
            <SpotlightCard key={c.title} icon={c.icon} title={c.title} body={c.body} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeatureGrid;
